/**
 * ApiCallLogSection — chronological list of last 50 fetch() calls with
 * status/duration coloring + click-to-expand for headers + bodies.
 */
import { useState } from 'react';
import { DebugJson, DebugSection } from '../DebugSection';
import { useApiCallLog } from '../hooks/useApiCallLog';
import { clearApiCallLog, computeLatencyStats } from '../services/apiCallLogStore';

export interface ApiCallLogSectionProps { searchTerm?: string }

export function ApiCallLogSection({ searchTerm }: ApiCallLogSectionProps): JSX.Element {
  const entries = useApiCallLog();
  const stats = computeLatencyStats();
  const [expanded, setExpanded] = useState<number | null>(null);

  const term = (searchTerm ?? '').trim().toLowerCase();
  const visible = term
    ? entries.filter((e) => `${e.method} ${e.url} ${e.responseStatus}`.toLowerCase().includes(term))
    : entries;

  const rightAction = (
    <button
      type="button"
      className="pcb-debug-action-btn"
      onClick={() => clearApiCallLog()}
      title="Clear API call log"
    >
      Clear
    </button>
  );

  return (
    <DebugSection
      id="api"
      title="4. API call log"
      count={entries.length}
      searchTerm={searchTerm}
      rightAction={rightAction}
    >
      <div className="pcb-debug-stat-row">
        <span><b>p50</b> {stats.p50}ms</span>
        <span><b>p99</b> {stats.p99}ms</span>
        <span><b>mean</b> {stats.mean}ms</span>
        <span><b>n</b> {stats.count}</span>
      </div>
      {visible.length === 0 ? (
        <div className="pcb-debug-empty">No requests captured yet.</div>
      ) : (
        <table className="pcb-debug-table">
          <thead>
            <tr>
              <th style={{ width: 60 }}>method</th>
              <th>path</th>
              <th style={{ width: 60 }}>status</th>
              <th style={{ width: 70 }}>ms</th>
              <th style={{ width: 100 }}>at</th>
            </tr>
          </thead>
          <tbody>
            {visible.slice().reverse().map((e) => {
              const isOpen = expanded === e.id;
              const tone = !e.ok ? 'is-err' : e.responseStatus >= 400 ? 'is-warn' : 'is-ok';
              return (
                <>
                  <tr
                    key={`row-${e.id}`}
                    className={`pcb-debug-table__row ${tone}`}
                    onClick={() => setExpanded(isOpen ? null : e.id)}
                  >
                    <td><code>{e.method}</code></td>
                    <td className="pcb-debug-truncate">{e.pathname}</td>
                    <td><code>{e.responseStatus || (e.error ? 'ERR' : '—')}</code></td>
                    <td>{e.durationMs}</td>
                    <td className="pcb-debug-truncate">{e.timestamp.slice(11, 19)}</td>
                  </tr>
                  {isOpen && (
                    <tr key={`detail-${e.id}`} className="pcb-debug-table__detail">
                      <td colSpan={5}>
                        <div className="pcb-debug-detail">
                          <div><b>URL</b><div className="pcb-debug-mono">{e.url}</div></div>
                          {e.functionKeyFingerprint && (
                            <div><b>Function key</b><div className="pcb-debug-mono">{e.functionKeyFingerprint}</div></div>
                          )}
                          {e.officerCode && (
                            <div><b>x-pcb-officer-code</b><div className="pcb-debug-mono">{e.officerCode}</div></div>
                          )}
                          <details>
                            <summary>request headers ({Object.keys(e.requestHeaders).length})</summary>
                            <DebugJson value={e.requestHeaders} />
                          </details>
                          {e.requestBody !== undefined && (
                            <details>
                              <summary>request body</summary>
                              <DebugJson value={e.requestBody} />
                            </details>
                          )}
                          <details open>
                            <summary>response body{e.responseBodyTruncated ? ' (truncated)' : ''}</summary>
                            <DebugJson value={e.responseBody} />
                          </details>
                          {e.error && (
                            <div className="pcb-debug-err">
                              <b>error</b>
                              <div className="pcb-debug-mono">{e.error}</div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      )}
    </DebugSection>
  );
}
